import type { THEMES } from "./constants";

export type ThemeColor = `#${string}`;

export const DEFAULT_THEME_COLOR = "#d92332" as ThemeColor;
export const THEME_COLOR_PATTERN = /^#[0-9a-f]{6}$/iu;

export const THEME_COLOR_PRESETS = [
  { name: "Signal", color: "#d92332" },
  { name: "Cobalt", color: "#2878f0" },
  { name: "Acid", color: "#79c94b" },
  { name: "Hot Pink", color: "#e44991" },
  { name: "Solar", color: "#e1b932" },
  { name: "Violet", color: "#8d68d8" },
  { name: "Orange", color: "#ef6c35" },
  { name: "Ice", color: "#5ac8d8" },
] as const satisfies ReadonlyArray<{ name: string; color: ThemeColor }>;

type LegacyTheme = (typeof THEMES)[number];

export const LEGACY_THEME_COLORS = {
  "signal-red": "#d92332",
  "electric-blue": "#3c8fef",
  "acid-green": "#79c94b",
  "hot-pink": "#e44991",
  "solar-yellow": "#e1b932",
  violet: "#8d68d8",
} as const satisfies Record<LegacyTheme, ThemeColor>;

interface Rgb {
  red: number;
  green: number;
  blue: number;
}

export interface ThemeTokens {
  color: ThemeColor;
  readable: ThemeColor;
  bright: ThemeColor;
  dark: ThemeColor;
  shadow: ThemeColor;
  onAccent: ThemeColor;
  soft: string;
  glow: string;
  background: ThemeColor;
  surface: ThemeColor;
  surfaceStrong: ThemeColor;
  controlSurface: ThemeColor;
  line: ThemeColor;
  lineSoft: ThemeColor;
  ink: ThemeColor;
  muted: ThemeColor;
}

export function isThemeColor(value: unknown): value is ThemeColor {
  return typeof value === "string" && THEME_COLOR_PATTERN.test(value);
}

export function normalizeThemeColor(
  value: unknown,
  fallback: ThemeColor = DEFAULT_THEME_COLOR,
): ThemeColor {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim().toLowerCase();
  return isThemeColor(normalized) ? normalized : fallback;
}

export function legacyThemeColor(theme: LegacyTheme): ThemeColor {
  return LEGACY_THEME_COLORS[theme];
}

function parseHex(value: ThemeColor): Rgb {
  return {
    red: Number.parseInt(value.slice(1, 3), 16),
    green: Number.parseInt(value.slice(3, 5), 16),
    blue: Number.parseInt(value.slice(5, 7), 16),
  };
}

function toHex({ red, green, blue }: Rgb): ThemeColor {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function mix(color: ThemeColor, target: ThemeColor, targetWeight: number): ThemeColor {
  const source = parseHex(color);
  const destination = parseHex(target);
  const weight = Math.min(1, Math.max(0, targetWeight));
  return toHex({
    red: source.red + (destination.red - source.red) * weight,
    green: source.green + (destination.green - source.green) * weight,
    blue: source.blue + (destination.blue - source.blue) * weight,
  });
}

function channelLuminance(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
}

function luminance(color: ThemeColor): number {
  const value = parseHex(color);
  return (
    channelLuminance(value.red) * 0.2126 +
    channelLuminance(value.green) * 0.7152 +
    channelLuminance(value.blue) * 0.0722
  );
}

export function contrastRatio(first: ThemeColor, second: ThemeColor): number {
  const light = Math.max(luminance(first), luminance(second));
  const dark = Math.min(luminance(first), luminance(second));
  return (light + 0.05) / (dark + 0.05);
}

function readableAccent(color: ThemeColor, background: ThemeColor): ThemeColor {
  if (contrastRatio(color, background) >= 4.5) return color;
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mix(color, "#f8f4ed", step / 20);
    if (contrastRatio(candidate, background) >= 4.5) return candidate;
  }
  return "#f8f4ed";
}

function textOnAccent(color: ThemeColor): ThemeColor {
  const dark = "#050505" as ThemeColor;
  const light = "#faf8f4" as ThemeColor;
  return contrastRatio(color, dark) >= contrastRatio(color, light) ? dark : light;
}

function alpha(color: ThemeColor, opacity: number): string {
  const value = parseHex(color);
  return `rgb(${value.red} ${value.green} ${value.blue} / ${Math.round(opacity * 100)}%)`;
}

export function createThemeTokens(value: unknown): ThemeTokens {
  const color = normalizeThemeColor(value);
  const background = mix("#151313", color, 0.06);
  return {
    color,
    readable: readableAccent(color, background),
    bright: mix(color, "#f8f4ed", 0.22),
    dark: mix(color, "#111010", 0.58),
    shadow: mix(color, "#090808", 0.78),
    onAccent: textOnAccent(color),
    soft: alpha(color, 0.15),
    glow: alpha(color, 0.24),
    background,
    surface: mix("#1d1a1a", color, 0.09),
    surfaceStrong: mix("#262121", color, 0.13),
    controlSurface: mix("#111010", color, 0.06),
    line: mix("#4b4240", color, 0.25),
    lineSoft: mix("#302b2a", color, 0.18),
    ink: mix("#f2ece3", color, 0.035),
    muted: mix("#aaa19a", color, 0.08),
  };
}

export function themeCssVariables(value: unknown): Record<`--${string}`, string> {
  const tokens = createThemeTokens(value);
  return {
    "--theme-color": tokens.color,
    "--theme-readable": tokens.readable,
    "--theme-bright": tokens.bright,
    "--theme-dark": tokens.dark,
    "--theme-shadow": tokens.shadow,
    "--theme-on-accent": tokens.onAccent,
    "--theme-soft": tokens.soft,
    "--theme-glow": tokens.glow,
    "--theme": tokens.readable,
    "--red": tokens.color,
    "--red-dark": tokens.dark,
    "--red-bright": tokens.readable,
    "--bg": tokens.background,
    "--surface": tokens.surface,
    "--surface-strong": tokens.surfaceStrong,
    "--control-surface": tokens.controlSurface,
    "--line": tokens.line,
    "--line-soft": tokens.lineSoft,
    "--ink": tokens.ink,
    "--muted": tokens.muted,
    "--focus": tokens.readable,
  };
}
